import React, { useState } from 'react';
import { fetchCardsByYear, saveCustomCard } from '../../api/cards';
import GLOBALS from '../../config/globals';
import Card from '../../components/Card';
import useSeries from '../../hooks/useSeries';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminCustomCardPage = () => {
    const [year, setYear] = useState('');
    const [cards, setCards] = useState([]);
    const series = useSeries();
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedSeries, setSelectedSeries] = useState('');
    const [customImage, setCustomImage] = useState('');
    const [artistID, setArtistID] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const handleYearChange = async e => {
        const selectedYear = e.target.value;
        setYear(selectedYear);
        setSelectedCard(null);
        setCards([]);
        setLoading(true);
        setError(null);

        try {
            const data = await fetchCardsByYear(selectedYear);
            setCards(data);
        } catch (error) {
            setError('Error fetching cards for the selected year');
            console.error('Error fetching cards:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCardChange = e => {
        const cardID = e.target.value;
        const card = cards.find(c => c.cardID == cardID);
        setSelectedCard(card);
    };

    const handleSave = async () => {
        if (!selectedCard || !customImage) {
            setError('Card and image are required');
            return;
        }

        try {
            setError(null);
            setLoading(true);
            const payload = {
                cardID: selectedCard.cardID,
                customImage,
                artistID: artistID || null,
                description: description || null,
                seriesID: selectedSeries || null, // Include seriesID if selected, otherwise null
            };
            await saveCustomCard(payload);
            setSuccessMessage('Custom card saved successfully!');
        } catch (error) {
            setError('Error saving custom card');
            console.error('Error saving custom card:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-custom-card-page admin-page">
            <h2>Custom Card</h2>

            <div className="admin-selector">
                <label htmlFor="yearSelect">Year:</label>
                <select id="yearSelect" value={year} onChange={handleYearChange}>
                    <option value="">Select a year</option>
                    {Array.from(
                        { length: GLOBALS.LAST_YEAR - GLOBALS.FIRST_YEAR },
                        (_, i) => GLOBALS.FIRST_YEAR + i
                    ).map(y => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </select>
            </div>

            {loading && <LoadingSpinner />}
            {error && <p className="admin-error">{error}</p>}
            {successMessage && <p className="admin-success">{successMessage}</p>}

            {!loading && year && cards.length > 0 && (
                <>
                    <div className="admin-selector">
                        <label htmlFor="cardSelect">Card:</label>
                        <select id="cardSelect" onChange={handleCardChange}>
                            <option value="">Select a card</option>
                            {cards.map(card => (
                                <option key={card.cardID} value={card.cardID}>
                                    ({card.rating}) {card.name} ({card.year})
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedCard && (
                        <div className="selected-card">
                            <Card card={selectedCard} />
                        </div>
                    )}

                    {selectedCard && (
                        <div className="admin-form">
                            <div className="admin-field">
                                <label htmlFor="customImage">Image URL</label>
                                <input
                                    type="text"
                                    id="customImage"
                                    value={customImage}
                                    onChange={e => setCustomImage(e.target.value)}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="artistID">Artist ID</label>
                                <input
                                    type="text"
                                    id="artistID"
                                    value={artistID}
                                    onChange={e => setArtistID(e.target.value)}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            <div className="admin-field">
                                <label htmlFor="seriesSelect">Series (Optional)</label>
                                <select
                                    id="seriesSelect"
                                    value={selectedSeries}
                                    onChange={e => setSelectedSeries(e.target.value)}
                                >
                                    <option value="">No series</option>
                                    {series.map(ser => (
                                        <option key={ser.seriesID} value={ser.seriesID}>
                                            {ser.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button onClick={handleSave} disabled={loading}>
                                Save Custom Card
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminCustomCardPage;
